import { Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IconModule } from '@insolution/core-data-access';
import { Announcement, AnnouncementMessages } from '@insolution/graphql-data-access';
import { LocalDateFormatPipe } from '@insolution/language-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { ConfirmDeleteAnnouncement } from '../confirm-delete-announcement/confirm-delete-announcement';

interface AnnouncementsListRow {
  announcement: Announcement;
  message: string;
}

@Component({
  selector: 'lib-announcements-list',
  imports: [TranslatePipe, LocalDateFormatPipe, ConfirmDeleteAnnouncement, IconModule],
  templateUrl: './announcements-list.html',
  styleUrl: './announcements-list.scss',
})
export class AnnouncementsList {
  public announcements = input.required<Announcement[]>();

  public create = output<void>();
  public edit = output<Announcement>();
  public deleteAnnouncement = output<Announcement>();

  private translateService = inject(TranslateService);

  private currentLanguage = toSignal(this.translateService.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translateService.currentLang,
  });

  protected rows = computed<AnnouncementsListRow[]>(() =>
    this.announcements().map((announcement) => ({
      announcement,
      message: this.messageForCurrentLanguage(announcement.messages),
    }))
  );

  protected onCreate(): void {
    this.create.emit();
  }

  protected onEdit(announcement: Announcement): void {
    this.edit.emit(announcement);
  }

  protected onDelete(announcement: Announcement): void {
    this.deleteAnnouncement.emit(announcement);
  }

  private messageForCurrentLanguage(messages: AnnouncementMessages): string {
    const messageKey = (this.currentLanguage() ?? 'en-US').replace('-', '_') as keyof AnnouncementMessages;

    return messages[messageKey] ?? messages.en_US;
  }
}
